/**
 * Cliente Web Push do frontend.
 *
 * Fluxo de ativacao:
 *  1. getPushStatus() → 'unsupported' | 'denied' | 'granted-subscribed' |
 *     'granted-not-subscribed' | 'default'
 *  2. enablePush() → pede permissao, subscribe, POST /api/push/subscribe.
 *  3. disablePush() → unsubscribe local + DELETE /api/push/subscribe.
 *
 * Caveat iOS: push so funciona em PWA instalado (Adicionar a Tela de Inicio)
 * a partir do iOS 16.4. Em Safari mobile normal o subscribe falha com
 * NotAllowedError — tratamos a falha graciosamente.
 */

import api from '../api';
import { logPwaEvent, getDisplayMode } from './telemetry';

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(base64);
  const arr = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i += 1) arr[i] = raw.charCodeAt(i);
  return arr;
}

export function isPushSupported() {
  return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
}

export async function getPushStatus() {
  if (!isPushSupported()) return 'unsupported';
  const perm = Notification.permission;
  if (perm === 'denied') return 'denied';
  if (perm === 'default') return 'default';
  try {
    const reg = await navigator.serviceWorker.ready;
    const sub = await reg.pushManager.getSubscription();
    return sub ? 'granted-subscribed' : 'granted-not-subscribed';
  } catch (e) {
    logPwaEvent('push-status-error', { msg: e.message });
    return 'default';
  }
}

async function getServerConfig() {
  const res = await api.get('/api/push/config');
  return res.data || {};
}

export async function enablePush() {
  if (!isPushSupported()) {
    return { ok: false, reason: 'unsupported', displayMode: getDisplayMode() };
  }

  const perm = await Notification.requestPermission();
  if (perm !== 'granted') {
    logPwaEvent('push-permission-denied', { perm });
    return { ok: false, reason: 'permission-denied' };
  }

  const cfg = await getServerConfig();
  if (!cfg.enabled || !cfg.vapid_public_key) {
    return { ok: false, reason: 'server-disabled' };
  }

  const reg = await navigator.serviceWorker.ready;
  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    try {
      sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(cfg.vapid_public_key),
      });
    } catch (e) {
      logPwaEvent('push-subscribe-fail', { msg: e.message, name: e.name });
      return { ok: false, reason: `subscribe-${e.name || 'error'}` };
    }
  }

  const json = sub.toJSON();
  await api.post('/api/push/subscribe', {
    endpoint: json.endpoint,
    keys: json.keys,
  });

  logPwaEvent('push-enabled', { endpoint: json.endpoint.slice(0, 60) });
  return { ok: true };
}

export async function disablePush() {
  if (!isPushSupported()) return { ok: true };
  try {
    const reg = await navigator.serviceWorker.ready;
    const sub = await reg.pushManager.getSubscription();
    if (sub) {
      const endpoint = sub.endpoint;
      await sub.unsubscribe();
      try {
        await api.delete('/api/push/subscribe', { data: { endpoint } });
      } catch (_) { /* ignora — endpoint sera limpo no servidor por 410 */ }
    }
    logPwaEvent('push-disabled');
    return { ok: true };
  } catch (e) {
    logPwaEvent('push-disable-fail', { msg: e.message });
    return { ok: false, reason: e.message };
  }
}

export async function sendTestPush() {
  try {
    const res = await api.post('/api/push/test');
    return { ok: true, ...res.data };
  } catch (e) {
    return { ok: false, status: e?.response?.status };
  }
}
